import { useEffect } from 'react';
import type { RootOptions } from 'react-dom/client';

import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { PayloadAction, StoreEnhancer } from '@reduxjs/toolkit';

import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { TransactionState } from '@/features/paymentProcess/transactionSlice';
import { UserDetailsState } from '@/features/userDetails/userDetailsSlice';

const getSentryEnvConfig = (): { dsn: string; environment: string } | null => {
  const sentryDsn: string | undefined = import.meta.env.VITE_SENTRY_DSN;

  // While Sentry will look for SENTRY_ENVIRONMENT by default...
  // (https://docs.sentry.io/platforms/php/configuration/environments/)
  // ... I figured it'd be nicer to keep this in line with our other Vite-flavoured variables,
  // and let us only use it conditionally to keep both Sentry and TS happy:
  const sentryEnv: string | undefined = typeof import.meta.env.VITE_SENTRY_ENV === 'string'
    ? import.meta.env.VITE_SENTRY_ENV
    : undefined;

  if (!sentryDsn || !sentryEnv) {
    return null;
  }

  return { dsn: sentryDsn, environment: sentryEnv };
};

/**
 * Pass these options as the second argument to createRoot so uncaught / recoverable
 * errors are reported (see @sentry/react's online documentation about reactErrorHandler).
 * onCaughtError is omitted here because the app uses Sentry.ErrorBoundary for subtree errors.
 */
export const getSentryReact19RootOptions = (): RootOptions | undefined => {
  if (!getSentryEnvConfig()) {
    return undefined;
  }

  return {
    onUncaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  };
};

export const sentrySetup = () => {
  const config = getSentryEnvConfig();

  // No logging to be done if we don't have either explictly set
  if (!config) {
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      // Sentry.captureConsoleIntegration(),
    ],
    tracesSampleRate: 1.0, // Capture 100% of all transactions for tracing
    tracePropagationTargets: ['localhost', /cr\.netlify\.app\//, /^\//], // Not enabling URLs trace propagation for CR APIs due to documented CORS error (sentry-trace and baggage headers added)
    replaysSessionSampleRate: 0.1, // Capture Replay for 10% of all sessions
    replaysOnErrorSampleRate: 1.0, // Capture Replay 100% of sessions with an error
  });
};

const actionPrefixDisallowList = [
  '@@REMEMBER_REHYDRATED',
  '@@REMEMBER_PERSISTED',
  'lookupsApi',
  'paymentsApi',
  'cartsApi',
];

// Redux enhancer to allow redux actions to be logged as "breadcrumbs" in Sentry
export const sentryEnhancer = () => (
  (Sentry.createReduxEnhancer({
    // Prevent Sentry logging over-active redux actions (rememberMiddleware & API slices)
    actionTransformer: (action: PayloadAction) => {
      const actionPrefix = action.type.split('/')[0];

      if (actionPrefixDisallowList.includes(actionPrefix)) {
        return null;
      }

      return action;
    },
    // Set Sentry tags based on Redux state variables (to make it easier to find patterns in errors)
    configureScopeWithState: (
      scope,
      state: {
        paymentSelect?: PaymentSelectState,
        transaction?: TransactionState,
        userDetails?: UserDetailsState,
      },
    ) => {
      if (state?.paymentSelect?.provider) {
        scope.setTag('provider', state.paymentSelect.provider);
      }
      if (state?.paymentSelect?.provider) {
        scope.setTag('provider', state.paymentSelect.provider);
      }
      if (state?.transaction?.transactionId) {
        scope.setTag('transactionId', state.transaction.transactionId);
      }
      if (state?.transaction?.subscriptionId) {
        scope.setTag('subscriptionId', state.transaction.subscriptionId);
      }
      if (state?.userDetails?.email) {
        scope.setUser({ email: state.userDetails.email });
      }
    },
  })) as StoreEnhancer
);
